import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Loader2,
  RefreshCw,
  FileDown,
  Check,
  X,
  Clock,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const inputClass =
  "w-full bg-[#040812]/70 border border-white/10 focus:border-red-500/60 focus:outline-none text-white placeholder-slate-500 px-4 py-3 text-sm rounded-sm transition-colors";

const StatusPill = ({ status }) => {
  const map = {
    pending: "bg-yellow-500/10 text-yellow-300 border-yellow-500/30",
    approved: "bg-green-500/10 text-green-300 border-green-500/30",
    rejected: "bg-red-500/10 text-red-300 border-red-500/30",
  };
  const cls = map[status] || map.pending;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border font-mono text-[10px] tracking-[0.14em] uppercase ${cls}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {status || "pending"}
    </span>
  );
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminApplications() {
  const { authHeader, apiBase, token } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [approvalResult, setApprovalResult] = useState(null); // {temporary_password, msa_reference, ...}
  const [busyId, setBusyId] = useState(null);
  const [tempPw, setTempPw] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/admin/vendor-applications`, {
        headers: authHeader(),
      });
      setApps(data);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to load applications",
      );
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeader]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (statusFilter === "all") return apps;
    return apps.filter((a) => a.status === statusFilter);
  }, [apps, statusFilter]);

  const counts = useMemo(() => {
    return {
      total: apps.length,
      pending: apps.filter((a) => a.status === "pending").length,
      approved: apps.filter((a) => a.status === "approved").length,
      rejected: apps.filter((a) => a.status === "rejected").length,
    };
  }, [apps]);

  const approve = async (app) => {
    setBusyId(app.id);
    try {
      const { data } = await axios.post(
        `${apiBase}/admin/vendor-applications/${app.id}/approve`,
        tempPw ? { temporary_password: tempPw } : {},
        { headers: authHeader() },
      );
      toast.success("Approved. Vendor account + MSA created.");
      setApprovalResult({ ...data, applicant: app });
      setTempPw("");
      await load();
      setSelected(null);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Approval failed",
      );
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (app) => {
    const reason = window.prompt(
      `Reject application from ${app.full_name}? (optional reason)`,
    );
    if (reason === null) return; // cancelled
    setBusyId(app.id);
    try {
      await axios.post(
        `${apiBase}/admin/vendor-applications/${app.id}/reject`,
        { reason: reason || null },
        { headers: authHeader() },
      );
      toast.success("Application rejected.");
      await load();
      setSelected(null);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Reject failed",
      );
    } finally {
      setBusyId(null);
    }
  };

  const downloadMsa = async (appId, reference) => {
    try {
      const res = await axios.get(
        `${apiBase}/admin/vendor-applications/${appId}/msa`,
        {
          headers: authHeader(),
          responseType: "blob",
        },
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${reference || "MSA"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("MSA download failed.");
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied to clipboard.");
  };

  return (
    <div data-testid="admin-applications-panel">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <span className="overline">/Admin · Vendor Applications</span>
          <h1 className="mt-4 font-display text-white text-4xl md:text-5xl tracking-tighter font-light leading-tight">
            Partner applications
          </h1>
          <p className="mt-3 text-slate-500 text-sm">
            Approve to auto-issue a Master Service Agreement and create a CRM
            login.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={load}
            disabled={loading}
            data-testid="applications-refresh"
            className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-white text-sm px-4 py-2.5 rounded-sm transition-colors disabled:opacity-60"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { k: counts.total, v: "Total", id: "app-stat-total", filter: "all" },
          { k: counts.pending, v: "Pending", id: "app-stat-pending", filter: "pending" },
          { k: counts.approved, v: "Approved", id: "app-stat-approved", filter: "approved" },
          { k: counts.rejected, v: "Rejected", id: "app-stat-rejected", filter: "rejected" },
        ].map((s) => (
          <button
            key={s.v}
            data-testid={s.id}
            onClick={() => setStatusFilter(s.filter)}
            className={`text-left bg-[#0B132B]/50 border p-6 transition-colors ${
              statusFilter === s.filter
                ? "border-red-500"
                : "border-white/10 hover:border-white/25"
            }`}
          >
            <div className="font-display text-white text-3xl">{s.k}</div>
            <div className="mt-2 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase">
              {s.v}
            </div>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="mt-8 bg-[#0B132B]/40 border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left">
                {["Applied", "Applicant", "Company", "Services", "Status", "Actions"].map(
                  (h) => (
                    <th
                      key={h}
                      className="px-6 py-4 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center">
                    <Loader2 size={20} className="inline animate-spin text-slate-500" />
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-16 text-center text-slate-500 text-sm"
                  >
                    {apps.length === 0
                      ? "No applications yet. Applications from /partner will appear here."
                      : "No applications match your filter."}
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((app) => (
                  <tr
                    key={app.id}
                    data-testid={`app-row-${app.id}`}
                    onClick={() => setSelected(app)}
                    className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 text-slate-400 font-mono text-xs whitespace-nowrap">
                      {formatDate(app.created_at)}
                    </td>
                    <td className="px-6 py-4 text-white">
                      <div>{app.full_name}</div>
                      <div className="text-slate-500 text-xs">{app.email}</div>
                    </td>
                    <td className="px-6 py-4 text-slate-300 text-sm">
                      {app.company || "—"}
                      <div className="text-slate-500 text-xs mt-0.5">
                        {[app.city, app.country].filter(Boolean).join(", ") || ""}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-400 text-xs">
                      {(app.services || []).slice(0, 2).join(", ")}
                      {(app.services || []).length > 2
                        ? ` +${app.services.length - 2}`
                        : ""}
                    </td>
                    <td className="px-6 py-4">
                      <StatusPill status={app.status} />
                    </td>
                    <td className="px-6 py-4 text-right">
                      {app.status === "approved" && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadMsa(app.id, app.msa_reference);
                          }}
                          data-testid={`app-download-msa-${app.id}`}
                          className="inline-flex items-center gap-1 text-slate-400 hover:text-white text-xs"
                        >
                          <FileDown size={12} /> MSA
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drawer */}
      {selected && (
        <div
          data-testid="app-detail-drawer"
          className="fixed inset-0 z-50 flex justify-end"
          onClick={() => setSelected(null)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-none" />
          <div
            className="relative w-full max-w-xl bg-[#0B132B] border-l border-white/10 h-full overflow-y-auto p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">Application detail</div>
                <h2 className="mt-3 font-display text-white text-2xl tracking-tight">
                  {selected.full_name}
                </h2>
                <div className="mt-2 flex items-center gap-2">
                  <StatusPill status={selected.status} />
                  <span className="text-slate-500 text-xs">
                    · Applied {formatDate(selected.created_at)}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-slate-400 hover:text-white text-sm"
              >
                Close
              </button>
            </div>

            <dl className="mt-6 space-y-3 text-sm">
              <div>
                <dt className="overline mb-1">Email</dt>
                <dd className="text-white">
                  <a href={`mailto:${selected.email}`} className="hover:text-red-400">
                    {selected.email}
                  </a>
                </dd>
              </div>
              {selected.phone && (
                <div>
                  <dt className="overline mb-1">Phone</dt>
                  <dd className="text-white">{selected.phone}</dd>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <dt className="overline mb-1">Vendor type</dt>
                  <dd className="text-white capitalize">{selected.vendor_type}</dd>
                </div>
                <div>
                  <dt className="overline mb-1">Location</dt>
                  <dd className="text-white">
                    {[selected.city, selected.country].filter(Boolean).join(", ") || "—"}
                  </dd>
                </div>
              </div>
              {selected.company && (
                <div>
                  <dt className="overline mb-1">Business</dt>
                  <dd className="text-white">{selected.company}</dd>
                </div>
              )}
              {selected.website && (
                <div>
                  <dt className="overline mb-1">Website / LinkedIn</dt>
                  <dd className="text-white break-all">{selected.website}</dd>
                </div>
              )}
              <div>
                <dt className="overline mb-1">Services requested</dt>
                <dd className="text-white text-sm">
                  <ul className="mt-1 space-y-1">
                    {(selected.services || []).map((s) => (
                      <li key={s} className="text-slate-300">
                        · {s}
                      </li>
                    ))}
                  </ul>
                </dd>
              </div>
              {selected.pitch && (
                <div>
                  <dt className="overline mb-1">Pitch</dt>
                  <dd className="text-slate-300 whitespace-pre-wrap leading-relaxed border-l-2 border-red-500/40 pl-4">
                    {selected.pitch}
                  </dd>
                </div>
              )}
              {selected.msa_reference && (
                <div>
                  <dt className="overline mb-1">MSA reference</dt>
                  <dd className="text-white font-mono text-xs flex items-center gap-2">
                    {selected.msa_reference}
                    <button onClick={() => copy(selected.msa_reference)}>
                      <Copy size={12} className="text-slate-500 hover:text-white" />
                    </button>
                  </dd>
                </div>
              )}
              {selected.reject_reason && (
                <div>
                  <dt className="overline mb-1 !text-red-500">Reject reason</dt>
                  <dd className="text-red-300 text-sm">{selected.reject_reason}</dd>
                </div>
              )}
            </dl>

            {selected.status === "pending" && (
              <div className="mt-8 pt-6 border-t border-white/5">
                <div className="overline mb-2">Approve applicant</div>
                <p className="text-slate-500 text-xs mb-3">
                  Approving will create the vendor login and issue a Master
                  Service Agreement (₹20,000 + GST/sale, 5th-of-month payouts).
                </p>
                <input
                  data-testid="app-temp-password"
                  type="text"
                  placeholder="Optional temporary password (auto-generated if blank)"
                  value={tempPw}
                  onChange={(e) => setTempPw(e.target.value)}
                  className={inputClass}
                />
                <div className="mt-3 flex items-center gap-3">
                  <button
                    onClick={() => approve(selected)}
                    disabled={busyId === selected.id}
                    data-testid="app-approve-btn"
                    className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-70 text-white text-sm font-medium px-5 py-2.5 rounded-sm"
                  >
                    {busyId === selected.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Check size={14} />
                    )}
                    Approve & issue MSA
                  </button>
                  <button
                    onClick={() => reject(selected)}
                    disabled={busyId === selected.id}
                    data-testid="app-reject-btn"
                    className="inline-flex items-center gap-2 border border-white/15 hover:border-red-500 text-slate-400 hover:text-red-500 text-sm px-5 py-2.5 rounded-sm"
                  >
                    <X size={14} /> Reject
                  </button>
                </div>
              </div>
            )}

            {selected.status === "approved" && (
              <div className="mt-8 pt-6 border-t border-white/5">
                <button
                  onClick={() => downloadMsa(selected.id, selected.msa_reference)}
                  className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium px-5 py-2.5 rounded-sm"
                >
                  <FileDown size={14} /> Download MSA
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Post-approval modal — show credentials once */}
      {approvalResult && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setApprovalResult(null)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-none" />
          <div
            className="relative w-full max-w-md bg-[#0B132B] border border-white/10 p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="approval-result-modal"
          >
            <Check size={20} className="text-red-500" />
            <span className="overline mt-4 block">Vendor approved</span>
            <h2 className="font-display text-white text-xl tracking-tight mt-3">
              Share these details securely
            </h2>
            <p className="text-slate-500 text-xs mt-2">
              This is the only time the temporary password is shown. Copy it
              now, then send via a secure channel.
            </p>

            <dl className="mt-6 space-y-3 text-sm">
              <div>
                <dt className="overline mb-1">Login URL</dt>
                <dd className="text-white flex items-center gap-2">
                  /crm/login
                  <button onClick={() => copy(`${window.location.origin}/crm/login`)}>
                    <Copy size={12} className="text-slate-500 hover:text-white" />
                  </button>
                </dd>
              </div>
              <div>
                <dt className="overline mb-1">Email</dt>
                <dd className="text-white flex items-center gap-2">
                  {approvalResult.email}
                  <button onClick={() => copy(approvalResult.email)}>
                    <Copy size={12} className="text-slate-500 hover:text-white" />
                  </button>
                </dd>
              </div>
              <div>
                <dt className="overline mb-1">Temporary password</dt>
                <dd className="text-white font-mono text-sm bg-[#040812]/70 border border-white/10 p-3 rounded-sm flex items-center justify-between">
                  <span>{approvalResult.temporary_password}</span>
                  <button onClick={() => copy(approvalResult.temporary_password)}>
                    <Copy size={12} className="text-slate-500 hover:text-white" />
                  </button>
                </dd>
              </div>
              <div>
                <dt className="overline mb-1">MSA reference</dt>
                <dd className="text-white font-mono text-xs flex items-center gap-2">
                  {approvalResult.msa_reference}
                  <button onClick={() => copy(approvalResult.msa_reference)}>
                    <Copy size={12} className="text-slate-500 hover:text-white" />
                  </button>
                </dd>
              </div>
            </dl>

            <div className="mt-6 flex items-center gap-3">
              <button
                onClick={() =>
                  downloadMsa(
                    approvalResult.application_id,
                    approvalResult.msa_reference,
                  )
                }
                data-testid="approval-download-msa"
                className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium px-5 py-2.5 rounded-sm"
              >
                <FileDown size={14} /> Download MSA
              </button>
              <button
                onClick={() => setApprovalResult(null)}
                className="text-slate-400 hover:text-white text-sm"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
